import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {LoginComponent} from "./pages/login/login.component";
import {ForgotPasswordComponent} from "./pages/forgot-password/forgot-password.component";
import {AuthGuard} from "./core/guards/auth.guard";
import {ResetPasswordComponent} from "./pages/reset-password/reset-password.component";
import {AccountStatusResolver} from "./resolver/account-status.resolver";
import {ChequeParameterResolver} from "./resolver/cheque-parameter.resolver";
import {AccountLevel1Guard} from "./core/guards/account-level-1.guard";
import {IdentityVerificationGuard} from "./core/guards/account-level-2.guard";
import {DocumentationGuard} from './core/guards/documentation.guard';
import {IncrementLimit3Guard} from './core/guards/increment-limit-3.guard';
import {AccountBelow2Guard} from './core/guards/account-below-2.guard';
import {DisabledGuard} from './core/guards/disabled.guard';
import {AdditionalInformationGuard} from "./core/guards/additional-information.guard";
import {ThankYouComponent} from './pages/thank-you/thank-you.component';
import {ThankYouGuard} from './core/guards/thank-you.guard';
import {UserProfileResolver} from './resolver/user-profile.resolver';
import {UserDiscountResolver} from './resolver/user-discount.resolver';
import { InvestorProfileGuard } from './core/guards/investor-profile.guard';
import { AuhtLayoutComponent } from './layout/auht-layout-component/auht-layout-component.component';
import { MainLayoutComponent } from './layout/main-layout-component/main-layout-component.component';
import { SellCheckGuard } from './core/guards/sell-check-guard.guard';
import { BankAccountsResolver } from './resolver/bank-accounts.resolver';

const routes: Routes = [
  {path:"",redirectTo:"home",pathMatch:"full"},
  {
    path: '',
    component: AuhtLayoutComponent,
    children: [
      {
        path:"login",
        component: LoginComponent
      },
      {
        path:"terminos-condiciones",
        loadChildren:() => import('./pages/terminos-condiciones/terminos-condiciones.module').then((m) => m.TerminosCondicionesModule),
      },
      {
        path:"register",
        loadChildren:() => import('./pages/register/register.module').then((m) => m.RegisterModule),
      },
      {
        path:"forgot-password",
        component:ForgotPasswordComponent
      },
      {
        path:"reset-password",
        component:ResetPasswordComponent
      },
      {
        path:"thank-you",
        component: ThankYouComponent,
        canActivate: [ThankYouGuard]
      }
    ]
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path:"home",
        loadChildren:() => import('./pages/home/home.module').then((m) => m.HomeModule),
      },
      {
        path:"dashboard",
        loadChildren:() => import('./pages/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path:"user-account-details",
        loadChildren:() => import('./pages/userdetails/userdetails.module').then((m) => m.UserdetailsModule),
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"perfil-publico",
        loadChildren:() => import('./pages/financing/public-profile.module').then((m) => m.FinancingModule),
        canActivate: [AccountLevel1Guard, DocumentationGuard, DisabledGuard],
        resolve: {
          accountStatus : AccountStatusResolver,
          userProfile: UserProfileResolver
        }
      },
      {
        path:"invertir/lineas-credito/pedido",
        loadChildren:() => import('./pages/financing/public-profile.module').then((m) => m.FinancingModule),
        canActivate: [InvestorProfileGuard, AccountLevel1Guard, DisabledGuard],
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"portfolio",
        loadChildren:() => import('./pages/portfolio/portfolio.module').then((m) => m.PortfolioModule),
        resolve: {
          chequesParameters: ChequeParameterResolver,
          userDiscount: UserDiscountResolver,
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"ofertas",
        loadChildren:() => import('./pages/ofertas/ofertas.module').then((m) => m.OfertasModule),
        canActivate: [AccountLevel1Guard, DisabledGuard],
        resolve: {
          accountStatus : AccountStatusResolver,
        }
      },
      {
        path:"invertir/cheques",
        loadChildren:() => import('./pages/cotizar/cotizar.module').then((m) => m.CotizarModule),
        canActivate: [InvestorProfileGuard, AccountLevel1Guard, DisabledGuard],
        resolve: {
          accountStatus : AccountStatusResolver,
          userProfile: UserProfileResolver
        }
      },
      {
        path:"invertir/lineas-credito",
        loadChildren:() => import('./pages/credit-lines/credit-lines.module').then((m) => m.CreditLinesModule),
        canActivate: [InvestorProfileGuard, AccountLevel1Guard, DisabledGuard],
        resolve: {
          accountStatus : AccountStatusResolver,
          userProfile: UserProfileResolver
        }
      },
      {
        path:"descontar",
        loadChildren:() => import('./pages/descontar/descontar.module').then((m) => m.DescontarModule),
        canActivate: [AccountLevel1Guard, DocumentationGuard,DisabledGuard, SellCheckGuard],
        resolve: {
          chequesParameters: ChequeParameterResolver,
          userDiscount: UserDiscountResolver,
          accountStatus : AccountStatusResolver,
          bankAccounts: BankAccountsResolver
        }
    
      },
      {
        path:"recibos-facturas",
        loadChildren:() => import('./pages/billing/billing.module').then((m) => m.BillingModule),
        canActivate: [DisabledGuard],
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"message-list",
        loadChildren:() => import('./pages/mensaje-list/mensaje-list.module').then((m) => m.MensajeListModule)
      },
      {
        path:"order-list",
        loadChildren:() => import('./pages/order-list/order-list.module').then((m) => m.OrderListModule),
        canActivate: [DisabledGuard]
      },
      {
        path:"transaccion-list",
        loadChildren:() => import('./pages/transaccion-list/transaccion-list.module').then((m) => m.TransaccionListModule),
        canActivate: [DisabledGuard]
      },
      {
        path:"movimiento-list",
        loadChildren:() => import('./pages/movimiento-list/movimiento-list.module').then((m) => m.MovimientoListModule),
        canActivate: [DisabledGuard]
      },
      {
        path:"verify-account",
        loadChildren:() => import('./pages/verify-account/verify-account.module').then((m) => m.VerifyAccountModule),
      },
      {
        path:"verify-information",
        loadChildren:() => import('./pages/verify-information/verify-information.module').then((m) => m.VerifyInformationModule),
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"verify-information-fisica",
        loadChildren:() => import('./pages/verify-information-fisica/verify-information-fisica.module').then((m) => m.VerifyInformationFisicaModule),
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"verify-additional-information",
        loadChildren:() => import('./pages/verify-additional-information/verify-additional-information.module').then((m) => m.VerifyAdditionalInformationModule),
        resolve: {
          accountStatus : AccountStatusResolver
        },
        canActivate: [AdditionalInformationGuard]
      },
      {
        path:"verify-additional-information-juridica",
        loadChildren:() => import('./pages/verify-additional-information-juridica/verify-additional-information-juridica.module').then((m) => m.VerifyAdditionalInformationJuridicaModule),
        resolve: {
          accountStatus : AccountStatusResolver
        },
        canActivate: [AdditionalInformationGuard]
      },
      {
        path:"increment-limit",
        loadChildren:() => import('./pages/increment-limit/increment-limit.module').then((m) => m.IncrementLimitModule),
        canActivate: [IdentityVerificationGuard, IncrementLimit3Guard, AccountBelow2Guard, DisabledGuard  ],
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path:"referral",
        loadChildren:() => import('./pages/referral/referral.module').then((m) => m.ReferralModule),
      },
      {
        path:"instructive",
        loadChildren:() => import('./pages/instructive/instructive.module').then((m) => m.InstructiveModule),
      },
      {
        path: "mi-documentacion",
        loadChildren: () => import('./pages/mi-documentacion/mi-documentacion.module').then((m) => m.MiDocumentacionModule),
        resolve: {
          accountStatus : AccountStatusResolver
        }
      },
      {
        path: "contacto",
        loadChildren: () => import('./pages/contacto/contacto.module').then((m) => m.ContactoModule),
        resolve: {
          accountStatus : AccountStatusResolver
        }
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
